export function runtimeRoomToHub(room) {
  const metadata = room?.runtime_metadata && typeof room.runtime_metadata === "object" ? room.runtime_metadata : {};

  return {
    ...room,
    id: room?.runtime_room_id,
    hub_id: room?.runtime_room_id,
    room_id: room?.id,
    room_size: metadata.room_size,
    user_data: metadata.user_data || {},
    name: room?.name,
    description: room?.description
  };
}

export function runtimeRoomCreator(room) {
  const owner = room?.owner_profile;
  if (!owner || typeof owner !== "object" || typeof owner.display_name !== "string" || !owner.display_name.trim()) {
    return null;
  }

  return {
    identityUserId: owner.identity_user_id ?? null,
    displayName: owner.display_name,
    profileKey: owner.profile_key ?? null,
    imageUrl: owner.profile_image_url ?? null
  };
}

export function hubSettingsToRuntimeUpdate(settings) {
  const metadata = {};
  if (Object.prototype.hasOwnProperty.call(settings || {}, "room_size")) metadata.room_size = settings.room_size;
  if (settings?.user_data && typeof settings.user_data === "object") metadata.user_data = settings.user_data;

  return {
    ...(typeof settings?.name === "string" ? { name: settings.name } : {}),
    ...(Object.prototype.hasOwnProperty.call(settings || {}, "description")
      ? { description: settings.description }
      : {}),
    ...(Object.keys(metadata).length ? { runtime_metadata: metadata } : {})
  };
}

export async function applyRoomDeploymentSelection(dashboardChannel, hubChannel, entry) {
  await dashboardChannel.updateHubDeployment(entry);
  hubChannel.updateScene(entry.url);
}

export function createPlatformRoomApi({ request, tenantHeaders = () => ({}) }) {
  const shareUrlCache = new Map();

  return {
    getRuntimeRoom(runtimeRoomId) {
      return request(`/runtime/rooms/${runtimeRoomId}`, "GET", undefined, {
        throwOnError: true
      });
    },

    updateRuntimeRoom(runtimeRoomId, settings) {
      return request(`/runtime/rooms/${runtimeRoomId}`, "PUT", hubSettingsToRuntimeUpdate(settings));
    },

    requestRuntimeSession(runtimeRoomId, input) {
      return request(`/runtime/rooms/${runtimeRoomId}/sessions`, "POST", input);
    },

    requestRuntimeAllocationGrant(runtimeRoomId) {
      return request(`/runtime/rooms/${runtimeRoomId}/allocation-grant`, "POST", undefined, { throwOnError: true });
    },

    async changeRoomDeployment(hub, selection) {
      const workspaceId = hub?.workspace_id;
      const roomId = hub?.room_id;
      if (!workspaceId) {
        throw new Error("A Workspace id is required to change a Room deployment.");
      }
      if (!roomId) {
        throw new Error("A Room id is required to change its deployment.");
      }

      let deploymentId = selection?.deployment_id || selection?.deploymentId;
      if (!deploymentId) {
        const providerResourceId = selection?.id;
        if (!providerResourceId) {
          throw new Error("A deployment id or provider Scene id is required to change a Room deployment.");
        }

        const catalog = await request(
          `/workspaces/${encodeURIComponent(
            workspaceId
          )}/deployments?target_key=room&status=active&provider_resource_id=${encodeURIComponent(
            providerResourceId
          )}&per_page=1`,
          "GET",
          undefined,
          { throwOnError: true }
        );
        deploymentId = catalog?.data?.[0]?.id;
      }

      if (!deploymentId) {
        throw new Error("No active Room deployment matches the selected provider Scene.");
      }

      return request(
        `/workspaces/${encodeURIComponent(workspaceId)}/rooms/${encodeURIComponent(roomId)}/deployment`,
        "PUT",
        { deployment_id: deploymentId },
        { throwOnError: true }
      );
    },

    async shareRoom(room) {
      const cleanRoomId = room?.room_id;
      const workspaceId = room?.workspace_id;
      if (!cleanRoomId || !workspaceId) {
        return null;
      }

      const headers = tenantHeaders();
      const cacheKey = `${headers["X-Tenant-Host"] || ""}:${workspaceId}:${cleanRoomId}`;
      const cached = shareUrlCache.get(cacheKey);
      if (cached) {
        return cached;
      }

      const share = await request(`/workspaces/${workspaceId}/rooms/${cleanRoomId}/share`, "POST", undefined, {
        headers
      });
      const shareUrl = share?.short_url || share?.url || null;
      if (shareUrl) {
        shareUrlCache.set(cacheKey, shareUrl);
      }

      return shareUrl;
    },

    async shareRuntimeRoom(runtimeRoomId) {
      if (!runtimeRoomId) {
        return null;
      }

      const headers = tenantHeaders();
      const cacheKey = `${headers["X-Tenant-Host"] || ""}:runtime:${runtimeRoomId}`;
      const cached = shareUrlCache.get(cacheKey);
      if (cached) {
        return cached;
      }

      const share = await request(
        `/public/rooms/runtime/${encodeURIComponent(runtimeRoomId)}/share`,
        "POST",
        undefined,
        { headers }
      );
      const shareUrl = share?.short_url || share?.url || null;
      if (shareUrl) {
        shareUrlCache.set(cacheKey, shareUrl);
      }

      return shareUrl;
    }
  };
}
